import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { aiPrompt } from '../utils/aiClient'
import { analyzeConfounds, confoundPromptFragment, CONFOUND_ENABLED } from '../utils/confoundAnalysis'
import { SCALE_DIRECTION } from '../utils/trackRecommendation'
import { localDateString } from '../utils/dateUtils'


const CONTEXT_OPTIONS = [
  'I traveled or was away from home',
  'I ate out more than usual',
  'I had a stressful week',
  'I wasn\'t feeling well this week',
  'I tried a new safe food',
  'I had a social event with limited food options',
  'My sleep was off',
  'Nothing unusual, a normal week',
]

const PAGE_CSS = `
  .sn-input { background: #FFFFFF; border: none; box-shadow: var(--shadow-1), inset 0 1px 3px rgba(56,62,48,0.05); transition: box-shadow var(--t-fast) var(--ease); }
  .sn-input:focus { outline: none; box-shadow: 0 0 0 3px rgba(61,92,60,0.30), var(--shadow-1); }
`

const s = {
  wrap: { minHeight: '100vh', background: 'transparent', display: 'flex', flexDirection: 'column' },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(8px)', boxShadow: 'var(--shadow-1)', position: 'sticky', top: 0, zIndex: 10 },
  logo: { fontFamily: 'Fraunces, serif', fontSize: '19px', fontWeight: 500, color: '#20241F' },
  logoEm: { color: '#3D5C3C', fontStyle: 'italic' },
  back: { fontSize: '13px', color: '#73736A', cursor: 'pointer', background: 'none', border: 'none' },
  weekBadge: { fontSize: '11px', color: '#3D5C3C', background: '#EDF3ED', padding: '4px 10px', borderRadius: '999px', fontWeight: 600 },
  content: { flex: 1, padding: '28px 24px 100px', maxWidth: '560px', margin: '0 auto', width: '100%' },
  eyebrow: { display: 'block', marginBottom: '10px' },
  title: { fontFamily: 'Fraunces, serif', fontSize: '30px', fontWeight: 300, marginBottom: '6px', letterSpacing: '-0.3px' },
  titleEm: { fontStyle: 'italic', color: '#3D5C3C' },
  hint: { fontSize: '13px', color: '#73736A', marginBottom: '28px', lineHeight: 1.65 },
  sectionDivider: { display: 'flex', alignItems: 'center', gap: '12px', margin: '28px 0 20px' },
  sectionLine: { flex: 1, height: '1px', background: 'rgba(0,0,0,0.07)' },
  sectionLabel: { whiteSpace: 'nowrap' },
  questionBlock: { marginBottom: '14px', padding: '18px 16px' },
  questionLabel: { fontFamily: 'Fraunces, serif', fontSize: '18px', fontWeight: 400, marginBottom: '12px', lineHeight: 1.3, color: '#20241F' },
  scaleLabels: { display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#73736A', marginBottom: '7px' },
  scaleRow: { display: 'flex', gap: '4px' },
  sbt: { flex: 1, height: '44px', borderRadius: '10px', border: 'none', background: '#EDF3ED', fontSize: '14px', cursor: 'pointer', fontWeight: 400, color: '#5A5A52', transition: 'all 0.12s' },
  sbtOn: { flex: 1, height: '44px', borderRadius: '10px', border: 'none', background: '#3D5C3C', fontSize: '18px', cursor: 'pointer', fontWeight: 400, color: 'white', fontFamily: 'Fraunces, serif', boxShadow: 'var(--shadow-pill)' },
  chipRow: { display: 'flex', gap: '7px', flexWrap: 'wrap' },
  chip: { padding: '10px 14px', borderRadius: '999px', border: 'none', boxShadow: 'var(--shadow-1)', background: '#FFFFFF', fontSize: '13px', fontWeight: 600, cursor: 'pointer', color: '#73736A', transition: 'all 0.12s', lineHeight: 1.3 },
  chipOn: { padding: '10px 14px', borderRadius: '999px', border: 'none', boxShadow: 'var(--shadow-pill)', background: '#3D5C3C', fontSize: '13px', fontWeight: 600, cursor: 'pointer', color: 'white', lineHeight: 1.3 },
  textarea: { width: '100%', borderRadius: '14px', padding: '12px 14px', fontSize: '16px', color: '#20241F', resize: 'none', height: '80px' },
  footer: { position: 'fixed', bottom: 0, left: 0, right: 0, padding: '16px 24px', background: 'rgba(251,248,242,0.92)', backdropFilter: 'blur(8px)', boxShadow: '0 -8px 24px rgba(56,62,48,0.06)' },
  cta: { width: '100%' },
  ctaDisabled: { width: '100%' },
}

// Build adaptive symptom scales based on what the user has baseline scores for
const getSymptomScales = (profile) => {
  const scales = []
  if (profile?.baseline_bloating) scales.push({ id: 'bloating', label: 'How was your bloating this week?', low: 'None at all', high: 'Severe', baseline: profile.baseline_bloating })
  if (profile?.baseline_gas) scales.push({ id: 'gas', label: 'How was your gas or cramping this week?', low: 'None at all', high: 'Severe', baseline: profile.baseline_gas })
  if (profile?.baseline_reflux) scales.push({ id: 'reflux', label: 'How was your reflux or heartburn this week?', low: 'None at all', high: 'Severe', baseline: profile.baseline_reflux })
  if (profile?.baseline_digestive) scales.push({ id: 'digestive', label: 'How was your overall digestive comfort this week?', low: 'Very uncomfortable', high: 'Very comfortable', baseline: profile.baseline_digestive })
  if (profile?.baseline_energy) scales.push({ id: 'energy', label: 'How were your energy levels this week?', low: 'Exhausted', high: 'Full energy', baseline: profile.baseline_energy })
  if (profile?.baseline_clarity) scales.push({ id: 'clarity', label: 'How was your mental clarity this week?', low: 'Very foggy', high: 'Crystal clear', baseline: profile.baseline_clarity })
  if (profile?.baseline_afternoon) scales.push({ id: 'afternoon', label: 'How were your afternoon energy levels this week?', low: 'Severe crash', high: 'Sustained energy', baseline: profile.baseline_afternoon })
  if (profile?.baseline_sleep) scales.push({ id: 'sleep', label: 'How was your sleep this week?', low: 'Very poor', high: 'Excellent', baseline: profile.baseline_sleep })
  if (profile?.baseline_wellbeing) scales.push({ id: 'wellbeing', label: 'How was your overall sense of wellbeing this week?', low: 'Very poor', high: 'Excellent', baseline: profile.baseline_wellbeing })
  // Fallback if no baselines set
  if (scales.length === 0) {
    scales.push({ id: 'bloating', label: 'How was your bloating this week?', low: 'None at all', high: 'Severe', baseline: null })
    scales.push({ id: 'energy', label: 'How were your energy levels this week?', low: 'Exhausted', high: 'Full energy', baseline: null })
  }
  return scales
}

const generateInsight = async ({ name, weekNumber, profile, answers, previousAnswers, currentFoods, phase, session }) => {
  const scales = getSymptomScales(profile)

  // ---- Daily adherence (descriptive only) ----
  // The insight may state these FACTS but never interpret daily factors —
  // causal connections are Layer 2's job, gated behind physician sign-off.
  let adherenceLine = ''
  let weekFactorRows = []
  if (session?.user?.id) {
    try {
      const since = new Date(); since.setDate(since.getDate() - 6)
      const sinceStr = localDateString(since)
      const { data: week } = await supabase.from('daily_factors')
        .select('log_date, followed_protocol, stress, sleep')
        .eq('user_id', session.user.id)
        .gte('log_date', sinceStr)
      weekFactorRows = week || []
      const logged = (week || []).length
      const followedDays = (week || []).filter(d => d.followed_protocol === true).length
      const answeredCompliance = (week || []).filter(d => d.followed_protocol !== null).length
      if (logged > 0) {
        adherenceLine = `\nDAILY CHECK-IN ADHERENCE THIS WEEK: logged ${logged} of 7 days` +
          (answeredCompliance > 0 ? `; followed the plan on ${followedDays} of ${answeredCompliance} logged days` : '') + '.'
      }
    } catch (e) { /* adherence never blocks the insight */ }
  }

  // ---- This week's symptom events (descriptive fuel for early-era insights) ----
  let eventsLine = ''
  if (session?.user?.id) {
    try {
      const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString()
      const { data: evs } = await supabase.from('symptom_logs').select('symptom, severity, note, logged_at').gte('logged_at', weekAgo).eq('user_id', session.user.id)
      if (evs && evs.length > 0) {
        const parts = evs.slice(0, 10).map(e => `${e.symptom}${e.note ? ' (' + e.note + ')' : ''}`)
        eventsLine = `\nTHIS WEEK'S LOGGED SYMPTOM EVENTS: ${parts.join('; ')}. Describe what the record shows (types, timing) in plain terms. Do NOT make causal claims about foods from these; verdicts are earned by cycles, never by weekly impressions.`

        // ---- Day-grain alignment (the connection layer) ------------------
        // The verdict survey already cross-references symptom days against
        // the daily record (noise floor, worst-sleep overlap). This gives
        // the weekly the same pre-computed overlap so the insight can name
        // a measured pattern in the user's OWN record without the user
        // having to say it. Stated as context, never as cause.
        try {
          const symptomDates = new Set(evs.map(e => String(e.logged_at).split('T')[0]))
          const sleepHours = (v) => {
            const n = parseFloat(v)
            if (!isNaN(n)) return n
            return ({ 'under6': 5.5, '6-7': 6.5, '7-8': 7.5, '8+': 8.5 })[v] ?? null
          }
          const rows = weekFactorRows.filter(r => r.stress || r.sleep)
          if (rows.length >= 4 && symptomDates.size > 0) {
            const isHighStress = (r) => ['high', 'severe'].includes(String(r.stress || '').toLowerCase())
            const sympRows = rows.filter(r => symptomDates.has(r.log_date))
            const clearRows = rows.filter(r => !symptomDates.has(r.log_date))
            const sympHigh = sympRows.filter(isHighStress).length
            const clearHigh = clearRows.filter(isHighStress).length
            const avg = (xs) => xs.length ? (xs.reduce((a, b) => a + b, 0) / xs.length) : null
            const sympSleep = avg(sympRows.map(r => sleepHours(r.sleep)).filter(x => x != null))
            const clearSleep = avg(clearRows.map(r => sleepHours(r.sleep)).filter(x => x != null))
            if (sympRows.length > 0) {
              eventsLine += `\nDAY-GRAIN ALIGNMENT (computed from the daily record, not from what the user said): of ${sympRows.length} symptom day${sympRows.length !== 1 ? 's' : ''} with factors logged, ${sympHigh} ${sympHigh === 1 ? 'was' : 'were'} high-stress; of ${clearRows.length} symptom-free logged day${clearRows.length !== 1 ? 's' : ''}, ${clearHigh} ${clearHigh === 1 ? 'was' : 'were'} high-stress.` +
                (sympSleep != null && clearSleep != null ? ` Sleep averaged ${sympSleep.toFixed(1)}h on symptom days vs ${clearSleep.toFixed(1)}h on clear days.` : '') +
                `\nALIGNMENT RULE: if the overlap is strong (most symptom days share a factor that symptom-free days lack), name it plainly as a measured overlap in their own record, as context worth watching, never as the cause. If the overlap is weak or mixed, do not mention factors at all.`
            }
          }
        } catch (e) { /* alignment never blocks the insight */ }

        // ---- Recall leads (symptom-night food memory, last 30 days) ------
        // Recalled-after-symptom foods are the weakest evidence species, so
        // the bar is high: a food must appear on 3+ symptom nights and on no
        // clear night to even be mentioned, and only ever as a lead.
        try {
          const since30 = new Date(Date.now() - 30 * 86400000).toISOString()
          const { data: recalls } = await supabase.from('meal_logs')
            .select('raw_text, foods, created_at')
            .eq('user_id', session.user.id)
            .gte('created_at', since30)
          const recallRows = (recalls || []).filter(m => (m.foods || []).some(f => f?.source === 'recall'))
          if (recallRows.length >= 3) {
            const { data: evs30 } = await supabase.from('symptom_logs').select('logged_at').eq('user_id', session.user.id).gte('logged_at', since30)
            const sympDays30 = new Set((evs30 || []).map(e => String(e.logged_at).split('T')[0]))
            const counts = {}
            for (const m of recallRows) {
              const day = String(m.created_at).split('T')[0]
              for (const f of (m.foods || [])) {
                if (!f?.name) continue
                const k = f.name.toLowerCase()
                counts[k] = counts[k] || { name: f.name, symp: 0, clear: 0 }
                if (sympDays30.has(day)) counts[k].symp += 1
                else counts[k].clear += 1
              }
            }
            const leads = Object.values(counts).filter(c => c.symp >= 3 && c.clear === 0)
            if (leads.length > 0) {
              const l = leads.sort((a, b) => b.symp - a.symp)[0]
              eventsLine += `\nRECALL LEAD (from the user's own symptom-night food notes, the weakest evidence species): ${l.name} appeared in their eaten-recently notes on ${l.symp} symptom nights this month and on no clear night.`
                + `\nRECALL RULE: you may name this once as a lead worth watching, in their own record's words, stamped as a lead and never a ruling. It must never sound like a verdict, never recommend removing the food, and if it is already on their protocol, say its 14 day trial will give the real answer.`
            }
          }
        } catch (e) { /* recall leads never block the insight */ }
      }
    } catch (e) {}
  }

  // ---- Active reintro cycle context (so the insight never misreads a test week) ----
  let cycleLine = ''
  if (session?.user?.id) {
    try {
      const { data: ac } = await supabase.from('reintroduction_results')
        .select('food, exposure_days_completed, washout_started_at')
        .eq('user_id', session.user.id).is('verdict', null)
        .order('started_at', { ascending: false }).limit(1).maybeSingle()
      if (ac) {
        const inWashout = !!ac.washout_started_at
        cycleLine = `\nACTIVE REINTRODUCTION CYCLE: currently testing ${ac.food} (${inWashout ? 'washout phase, food removed again' : `exposure phase, ${ac.exposure_days_completed || 0} of 3 exposure days logged`}).`
      }
    } catch (e) { /* cycle context never blocks the insight */ }
  }

  // ---- Layer 2: confound analysis (gated; surfaces at most one gentle note) ----
  let confoundFragment = ''
  if (CONFOUND_ENABLED && session?.user?.id) {
    try {
      const { data: dailyRows } = await supabase.from('daily_factors')
        .select('log_date, sleep, stress, hydration, drinks, followed_protocol')
        .eq('user_id', session.user.id)
      const { data: pastCheckins } = await supabase.from('weekly_checkins')
        .select('answers, submitted_at, week_number').eq('user_id', session.user.id)

      const mondayOf = (dt) => { const d = new Date(dt); const day = (d.getDay() + 6) % 7; const m = new Date(d); m.setDate(d.getDate() - day); return m.toISOString().split('T')[0] }
      // A check-in describes the PRECEDING protocol week (QA 2026-10-25 #5):
      // bucketing by submit date landed worse-weeks one Monday late against the
      // daily-factor weeks they describe. Key each one to the Monday of the
      // middle of the week it covers.
      const coveredWeekMonday = (weekNum, fallbackDate) => {
        const start = profile?.protocol_start_date
        if (!start || !weekNum) return mondayOf(fallbackDate || new Date())
        const [sy, sm, sd] = String(start).split('T')[0].split('-').map(Number)
        return mondayOf(new Date(sy, sm - 1, sd + (weekNum - 1) * 7 + 3))
      }
      const weeklySymptoms = (pastCheckins || []).map(c => ({
        weekStart: coveredWeekMonday(c.week_number, c.submitted_at),
        worseThanUsual: c.answers?.overall_feeling === 'Worse',
      }))
      weeklySymptoms.push({ weekStart: coveredWeekMonday(weekNumber), worseThanUsual: answers.overall_feeling === 'Worse' })

      const confoundResult = analyzeConfounds({ profile, dailyRows: dailyRows || [], weeklySymptoms })
      confoundFragment = confoundPromptFragment(confoundResult?.observation)

      if (confoundResult?.observation) {
        const obs = confoundResult.observation
        await supabase.from('confound_observations').insert({
          user_id: session.user.id,
          week_start: coveredWeekMonday(weekNumber),
          engine: obs.kind,
          factor: obs.factor,
          direction: obs.direction || null,
          repeats: obs.repeats || null,
          confidence: obs.confidence || null,
          // OFFERED to the AI, not yet surfaced (QA 2026-10-25 #6): the prompt
          // may omit it. handleSubmit flips this true only when the final
          // insight detectably carries the factor.
          surfaced: false,
          sufficiency: confoundResult.sufficiency?.state || null,
          detail: obs,
        })
      }
    } catch (e) { /* confound analysis never blocks the insight */ }
  }

  const symptomScores = scales
    .filter(s => answers[s.id] !== undefined)
    .map(s => {
      const baseline = s.baseline
      const current = answers[s.id]
      const change = baseline ? Math.round(((current - baseline) / baseline) * 100) : null
      // Direction matters: for some scales higher is BETTER (energy, clarity,
      // sleep, wellbeing), for others higher is WORSE (bloating, gas, reflux).
      const dir = SCALE_DIRECTION[s.id] === 'higherBetter' ? 'higher is better' : 'higher is worse'
      let trend = ''
      if (change != null && change !== 0) {
        const improved = SCALE_DIRECTION[s.id] === 'higherBetter' ? current > baseline : current < baseline
        trend = improved ? ' [IMPROVED]' : ' [WORSENED]'
      }
      return `${s.id}: ${current}/10 (${dir})${baseline ? ` (baseline: ${baseline}, ${change > 0 ? '+' : ''}${change}%${trend})` : ''}`
    }).join('\n')

  const previousScores = previousAnswers
    ? scales.filter(s => previousAnswers[s.id] !== undefined).map(s => `${s.id}: ${previousAnswers[s.id]}/10`).join(', ')
    : null

  const contextItems = answers.context_changes?.filter(c => c !== 'Nothing unusual, a normal week') || []
  const contextStr = contextItems.length > 0 ? contextItems.join(', ') : 'None flagged by user (this does not mean nothing happened)'

  const prompt = `You are the AI health coach inside Sensify, a food sensitivity wellness program. Generate a personalized weekly insight.

USER CONTEXT:
- Name: ${name}
- Week ${weekNumber} of the program
- Phase: ${phase || 'elimination'}
- Symptom focus: ${profile?.symptoms?.join(', ') || 'general wellness'}
- Foods being avoided: ${currentFoods?.slice(0, 8).join(', ') || 'their elimination list'}

THIS WEEK'S SCORES vs BASELINE:
${symptomScores}

${previousScores ? `LAST WEEK'S SCORES:\n${previousScores}` : 'This is their first check-in.'}

OVERALL FEELING: ${answers.overall_feeling || 'not specified'}
WHAT CHANGED THIS WEEK: ${contextStr}
COMPLIANCE: ${answers.compliance || 'not specified'}${adherenceLine}${cycleLine}${eventsLine}
${answers.notes ? `USER NOTES: ${answers.notes}` : ''}

YOUR TASK:
Write a personalized weekly insight. Rules:
- Maximum 3 sentences. Shorter is better.
- Lead with the single most significant change, with its specific number or percentage
- CRITICAL: each score is tagged "higher is better" or "higher is worse", and changes are tagged [IMPROVED] or [WORSENED]. Interpret direction correctly: a rising energy or clarity score is GOOD, a rising bloating or reflux score is BAD. Never call a worsening a win or an improvement a setback.
- Reference specific numbers and percentage changes, never be vague
- Factor in what changed this week (travel, stress, etc.) when explaining score shifts
- If an ACTIVE REINTRODUCTION CYCLE is listed and symptoms rose this week, attribute the rise to the food being tested rather than calling it a setback. That signal is the test doing its job. Never predict or assign the verdict, the cycle survey decides that.
- Voice: a sharp analyst who respects the reader. Direct, specific, calm. NOT a cheerleader, NOT a coach. Never use phrases like "wins you've earned", "keep crushing it", "you've got this", "momentum", or "journey"
- Never use em dashes or hyphens as punctuation. Use commas or periods instead
- Never say "based on your data" or "according to your responses"
- Use ${name}'s name only if it reads naturally, at most once
- If compliance was poor and scores are bad, you may note both facts side by side, but NEVER assert that adherence is 'the explanation' or 'the most plausible cause' of symptom changes. You cannot see everything that happened this week (alcohol, illness, sleep debt, life events), so never claim a definitive cause. Frame possible connections as possibilities, not conclusions.
- If DAILY CHECK-IN ADHERENCE is provided, you may include ONE short factual clause acknowledging it (e.g. "with all 7 days logged" or "on 4 of 5 logged days you followed the plan"). State it as fact only. Never scold about missed days, and never claim daily habits caused or explain symptom changes.
- NEVER say 'nothing unusual was reported' or imply the week was unremarkable. The context question captures only what the user chose to flag, not everything that happened. If context is empty, simply do not mention it.
- If this is the first check-in with data, compare against baseline scores only and do not reference prior weeks
${confoundFragment}

Write only the insight. No labels, no formatting.

CRITICAL STYLE RULE: Do not use the em dash character or use hyphens as sentence punctuation anywhere in your response. Restructure with commas, periods, or the word "and" instead.`

  const raw = await aiPrompt(prompt, 300)
  // Hard guarantee: convert any dash punctuation that slips through
  return raw
    .replace(/(\d)\s*[—–-]\s*(\d)/g, '$1 to $2')
    .replace(/\s+[—–]\s+/g, ', ')
    .replace(/\s+-\s+/g, ', ')
    .replace(/[—–]/g, ', ')
}

function ConfettiCanvas() {
  const ref = React.useRef(null)
  React.useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight
    const colors = ['#3D5C3C', '#8BAE8A', '#D4894A', '#FAF8F4', '#EDF3ED', '#4A8C6A']
    const pieces = Array.from({ length: 120 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height - canvas.height,
      w: Math.random() * 10 + 5, h: Math.random() * 5 + 3,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * 360, rotSpeed: (Math.random() - 0.5) * 4,
      vx: (Math.random() - 0.5) * 2, vy: Math.random() * 3 + 2,
    }))
    let start = null
    const duration = 3500
    let raf
    const animate = (ts) => {
      if (!start) start = ts
      const progress = Math.min((ts - start) / duration, 1)
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      pieces.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.rotation += p.rotSpeed
        const opacity = progress > 0.6 ? 1 - ((progress - 0.6) / 0.4) : 1
        ctx.save()
        ctx.globalAlpha = opacity
        ctx.translate(p.x + p.w / 2, p.y + p.h / 2)
        ctx.rotate((p.rotation * Math.PI) / 180)
        ctx.fillStyle = p.color
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h)
        ctx.restore()
      })
      if (progress < 1) raf = requestAnimationFrame(animate)
    }
    raf = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(raf)
  }, [])
  return <canvas ref={ref} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 999 }} />
}

export default function WeeklyCheckin({ session, weekNumber = 1, profile, currentFoods = [], phase = 'elimination', onComplete, onBack }) {
  const [answers, setAnswers] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [generatingInsight, setGeneratingInsight] = useState(false)
  const [insight, setInsight] = useState('')
  const [previousAnswers, setPreviousAnswers] = useState(null)
  const [insightError, setInsightError] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [showConfetti, setShowConfetti] = useState(false)
  const [streakMessage, setStreakMessage] = useState('')
  const [showStreakCard, setShowStreakCard] = useState(false)

  const name = session?.user?.user_metadata?.full_name?.split(' ')[0] || 'there'
  const symptomScales = getSymptomScales(profile)

  useEffect(() => { loadPreviousCheckin() }, [])

  const loadPreviousCheckin = async () => {
    if (weekNumber <= 1) return
    const { data } = await supabase.from('weekly_checkins').select('answers').eq('user_id', session.user.id).order('submitted_at', { ascending: false }).limit(1).maybeSingle()
    if (data) setPreviousAnswers(data.answers)
  }

  const setAnswer = (id, value) => setAnswers(prev => ({ ...prev, [id]: value }))

  const toggleContext = (item) => {
    const current = answers.context_changes || []
    if (item === 'Nothing unusual, a normal week') {
      setAnswer('context_changes', [item])
      return
    }
    const withoutNormal = current.filter(c => c !== 'Nothing unusual, a normal week')
    if (withoutNormal.includes(item)) {
      setAnswer('context_changes', withoutNormal.filter(c => c !== item))
    } else {
      setAnswer('context_changes', [...withoutNormal, item])
    }
  }

  const allRequiredAnswered = () => {
    const scalesDone = symptomScales.every(s => answers[s.id] !== undefined)
    const overallDone = answers.overall_feeling !== undefined
    const contextDone = answers.context_changes && answers.context_changes.length > 0
    const complianceDone = answers.compliance !== undefined
    return scalesDone && overallDone && contextDone && complianceDone
  }

  const handleSubmit = async () => {
    if (!allRequiredAnswered()) return
    setSubmitting(true)
    setSubmitError('')

    // Stamp the cycle context onto the record at write time. Context not captured
    // now is unrecoverable later; this is what makes honest filtering possible.
    let cycleTag = null
    try {
      const { data: tagCycle } = await supabase.from('reintroduction_results')
        .select('food, washout_started_at')
        .eq('user_id', session.user.id).is('verdict', null)
        .order('started_at', { ascending: false }).limit(1).maybeSingle()
      if (tagCycle) cycleTag = { food: tagCycle.food, phase: tagCycle.washout_started_at ? 'washout' : 'exposure' }
    } catch (e) {}

    // THE WEEK'S DATA LANDS FIRST (QA 2026-10-25 #1): the insert used to run
    // after the slow AI call with its result unchecked — a failure showed
    // "on the record" while the week silently vanished. Now the row is written
    // and verified before anything celebrates; the insight backfills after.
    const { data: inserted, error: insErr } = await supabase.from('weekly_checkins').insert({
      user_id: session.user.id,
      week_number: weekNumber,
      answers: { ...answers, _cycle: cycleTag, _phase: (phase === 'reintroduction' ? 'reintroduction' : 'elimination') },
      ai_insight: '',
      submitted_at: new Date().toISOString(),
    }).select().single()
    if (insErr || !inserted) {
      setSubmitError("Your check-in didn't save — your answers are still here. Tap Submit to try again.")
      setSubmitting(false)
      return
    }
    setSubmitted(true)
    setGeneratingInsight(true)

    await supabase.from('profiles').update({
      last_checkin_at: new Date().toISOString(),
      current_week: weekNumber,
    }).eq('id', session.user.id)

    let aiInsight = ''
    try {
      aiInsight = await generateInsight({ name, weekNumber, profile, answers, previousAnswers, currentFoods, phase, session })
      if (!aiInsight || aiInsight.trim().length === 0) throw new Error('empty insight')
      setInsight(aiInsight)
      // Backfill the saved row + the dashboard pointer — cosmetic, best-effort.
      try {
        await supabase.from('weekly_checkins').update({ ai_insight: aiInsight }).eq('id', inserted.id)
        await supabase.from('profiles').update({ latest_insight: aiInsight, latest_insight_week: weekNumber }).eq('id', session.user.id)
      } catch (e) {}
      // Mark this week's confound observation surfaced only if the insight
      // detectably carries it (QA 2026-10-25 #6: it used to claim surfaced on
      // merely being offered to a prompt allowed to omit it).
      try {
        const { data: obsRows } = await supabase.from('confound_observations')
          .select('id, factor, week_start').eq('user_id', session.user.id)
          .order('created_at', { ascending: false }).limit(1)
        const obs = obsRows && obsRows[0]
        if (obs) {
          const pat = obs.factor === 'alcohol' ? /alcohol|drink/i : new RegExp(obs.factor, 'i')
          if (pat.test(aiInsight)) {
            await supabase.from('confound_observations').update({ surfaced: true }).eq('id', obs.id)
          }
        }
      } catch (e) {}
    } catch (e) {
      aiInsight = '' // never persist junk
      setInsightError(true)
    }
    setGeneratingInsight(false)

    // Streak copy must be earned (QA 2026-10-25 #4): "N weeks clean" only when
    // the record actually shows N consecutive compliant check-ins.
    const goodCompliance = answers.compliance === 'Fully' || answers.compliance === 'Mostly'
    let consecutiveCompliant = 0
    try {
      const { data: hist } = await supabase.from('weekly_checkins')
        .select('week_number, answers').eq('user_id', session.user.id)
        .order('week_number', { ascending: false }).limit(12)
      let expect = weekNumber
      for (const row of (hist || [])) {
        if (row.week_number !== expect) break
        const comp = row.answers?.compliance
        if (comp === 'Fully' || comp === 'Mostly') { consecutiveCompliant++; expect-- } else break
      }
    } catch (e) { consecutiveCompliant = goodCompliance ? 1 : 0 }
    const earnedStreak = consecutiveCompliant >= weekNumber

    // Fire confetti if compliance was good
    if (goodCompliance) {
      setShowConfetti(true)
      setTimeout(() => {
        setShowConfetti(false)
        // Generate streak congratulations message
        const STREAK_MSGS = {
          1: `Week one done. That's the hardest stretch, and your body is already adjusting. Keep the same energy next week.`,
          2: `Two weeks of elimination on your record. The habit is set and the data is starting to mean something.`,
          3: `Three weeks in. This is where the signal starts getting clearer. Whatever you're noticing this week, that's real.`,
          4: `One month of compliance, every week of it on your record. The data you build over the next four weeks is what your verdicts get measured against.`,
          5: `Five weeks. You're deep into elimination now. The patterns are forming, and every logged week sharpens the picture.`,
          6: `Six weeks. Two more and reintroduction begins. You're closer to your Food Map than you've ever been.`,
          7: `Seven weeks clean. Almost there. The last stretch of elimination is the most important. Stay sharp.`,
          8: `Eight weeks done. Elimination complete. Everything you've built over the last two months is about to pay off.`,
        }
        const msg = earnedStreak
          ? (STREAK_MSGS[weekNumber] || `Week ${weekNumber} done, every week of it compliant. Keep going.`)
          : `Week ${weekNumber} logged — this one was a good week. Every honest check-in counts, compliant or not.`
        setStreakMessage(msg)
        setShowStreakCard(true)
      }, 3500)
    }

    setSubmitting(false)
  }

  // SUCCESS SCREEN
  if (submitted) {
    const firstScale = symptomScales[0]
    const secondScale = symptomScales[1]
    return (
      <div style={s.wrap}>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } } @keyframes slideUp { from { opacity:0; transform:translateY(20px) } to { opacity:1; transform:translateY(0) } }` + PAGE_CSS}</style>
        <div style={s.topBar}>
          <div style={{ width: 40 }} />
          <div style={s.logo}>sensi<em style={s.logoEm}>fy</em></div>
          <div style={s.weekBadge}>Week {weekNumber}</div>
        </div>
        <div className="sn-rise" style={s.content}>

          {/* Streak celebration card — appears after confetti */}
          {showStreakCard && (
            <div className="sn-hero" style={{ borderRadius: '24px', padding: '22px 20px', marginBottom: '16px', animation: 'slideUp 0.4s ease' }}>
              <div className="sn-micro sn-micro-dark" style={{ marginBottom: '8px' }}>Week {weekNumber} complete</div>
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.85)', lineHeight: 1.75, margin: 0 }}>{streakMessage}</p>
              <button onClick={() => setShowStreakCard(false)} style={{ position: 'absolute', top: '14px', right: '14px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}>×</button>
            </div>
          )}

          {(() => {
            // Tone-aware: did symptoms improve or dip this week? Drives the copy.
            const improvedCount = [firstScale, secondScale].filter(scale => {
              const cur = answers[scale?.id]; const bl = scale?.baseline
              if (bl == null || cur == null) return false
              const good = ['energy','clarity','afternoon','sleep','digestive','wellbeing'].includes(scale.id)
              return good ? cur > bl : cur < bl
            }).length
            const roughWeek = improvedCount === 0
            return (
              <div className="sn-hero" style={{ padding: '24px 22px', marginBottom: '14px' }}>
                <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                  <span className="sn-micro" style={{ color: '#8BAE8A' }}>SENSIFY · RECORD</span>
                  <span className="sn-micro sn-micro-dark" style={{ fontSize: 8.5 }}>WEEK {weekNumber} LOGGED</span>
                </div>
                <div style={{ position: 'relative', fontFamily: 'Fraunces, serif', fontSize: 26, fontWeight: 300, color: '#FAF8F4', lineHeight: 1.2, marginBottom: 4 }}>This week is <em style={{ fontStyle: 'italic', color: '#8BAE8A' }}>on the record.</em></div>
                <div style={{ position: 'relative', fontSize: 13, color: 'rgba(250,248,244,0.6)', marginBottom: 20 }}>Every honest week makes your map sharper.</div>

                {(() => {
                  // Display-scale payoff: the biggest computed change (vs the
                  // baseline already on record) leads at headline size; the
                  // remaining scores follow small. Numbers come straight from
                  // this check-in's answers, nothing is invented.
                  const goodIds = ['energy','clarity','afternoon','sleep','digestive','wellbeing']
                  const scored = symptomScales
                    .filter(scale => answers[scale.id] != null)
                    .map(scale => {
                      const cur = answers[scale.id]; const bl = scale.baseline
                      const good = goodIds.includes(scale.id)
                      const improved = bl != null ? (good ? cur > bl : cur < bl) : null
                      return { id: scale.id, cur, bl, improved, delta: bl != null ? Math.abs(cur - bl) : 0 }
                    })
                    .sort((a, b) => b.delta - a.delta)
                  if (scored.length === 0) return null
                  const lead = scored[0]
                  const rest = scored.slice(1, 3)
                  return (
                    <div style={{ position: 'relative', marginBottom: (insight || generatingInsight) ? 20 : 4 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                        <div className="sn-num" style={{ fontSize: 48, color: lead.improved === true ? '#8BAE8A' : '#FAF8F4' }}>{lead.cur}</div>
                        <div className="sn-micro sn-micro-dark">{lead.id.toUpperCase()}{lead.bl != null ? ` · WAS ${lead.bl}` : ''}</div>
                      </div>
                      {rest.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                          {rest.map(scale => (
                            <div key={scale.id} style={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: 12 }}>
                              <div className="sn-num" style={{ fontSize: 22, color: scale.improved === true ? '#8BAE8A' : '#FAF8F4' }}>{scale.cur}</div>
                              <div className="sn-micro sn-micro-dark" style={{ fontSize: 8, letterSpacing: '0.5px', marginTop: 4 }}>{scale.id.toUpperCase()}{scale.bl != null ? ` · WAS ${scale.bl}` : ''}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })()}

                {generatingInsight ? (
                  <div style={{ position: 'relative', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16, fontFamily: 'Fraunces, serif', fontStyle: 'italic', fontSize: 13, color: 'rgba(250,248,244,0.6)' }}>Writing your insight…</div>
                ) : insight ? (
                  <div style={{ position: 'relative', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16 }}>
                    <div className="sn-micro" style={{ color: '#8BAE8A', marginBottom: 8 }}>YOUR WEEK {weekNumber} INSIGHT</div>
                    <div style={{ fontSize: 13.5, color: '#FAF8F4', lineHeight: 1.7, fontWeight: 300 }}>{insight}</div>
                  </div>
                ) : insightError ? (
                  // Honest failure copy (QA 2026-10-25 #2): nothing retries or
                  // backfills a missed insight, so nothing is promised.
                  <div style={{ position: 'relative', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16, fontSize: 13.5, color: 'rgba(250,248,244,0.85)', lineHeight: 1.7, fontWeight: 300 }}>Your scores are saved and your trends updated. The written insight didn't generate this time — next week's will read this week's data too.</div>
                ) : null}
              </div>
            )
          })()}

          <button
            className="sn-btn"
            style={{ ...s.cta, marginTop: '8px' }}
            disabled={generatingInsight}
            onClick={onComplete}
          >{generatingInsight ? 'Saving your insight…' : 'Back to dashboard →'}</button>
        </div>
        {showConfetti && <ConfettiCanvas />}
      </div>
    )
  }

  return (
    <div style={s.wrap}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }` + PAGE_CSS}</style>
      <div style={s.topBar}>
        <button style={s.back} onClick={onBack}>← Back</button>
        <div style={s.logo}>sensi<em style={s.logoEm}>fy</em></div>
        <div style={s.weekBadge}>Week {weekNumber}</div>
      </div>
      <div className="sn-rise" style={s.content}>
        <div className="sn-micro" style={s.eyebrow}>Week {weekNumber} check-in</div>
        <div style={s.title}>How was your<br /><em style={s.titleEm}>week?</em></div>
        <div style={s.hint}>Honest answers only, good or bad. This is how we track what's actually working.</div>

        {/* ADAPTIVE SYMPTOM SCALES */}
        <div style={s.sectionDivider}>
          <div style={s.sectionLine} />
          <div className="sn-micro sn-micro-dim" style={s.sectionLabel}>Your symptoms</div>
          <div style={s.sectionLine} />
        </div>

        {symptomScales.map(scale => (
          <div key={scale.id} className="sn-card" style={s.questionBlock}>
            <div style={s.questionLabel}>{scale.label}</div>
            <div style={s.scaleLabels}><span>{scale.low}</span><span>{scale.high}</span></div>
            <div style={s.scaleRow}>
              {[1,2,3,4,5,6,7,8,9,10].map(n => (
                <button key={n} style={answers[scale.id] === n ? s.sbtOn : s.sbt} onClick={() => setAnswer(scale.id, n)}>{n}</button>
              ))}
            </div>
          </div>
        ))}

        {/* OVERALL FEELING */}
        <div style={s.sectionDivider}>
          <div style={s.sectionLine} />
          <div className="sn-micro sn-micro-dim" style={s.sectionLabel}>Overall</div>
          <div style={s.sectionLine} />
        </div>

        <div className="sn-card" style={s.questionBlock}>
          <div style={s.questionLabel}>How do you feel overall compared to last week?</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {['Better', 'About the same', 'Worse'].map(opt => (
              <button key={opt} style={answers.overall_feeling === opt ? { padding: '13px 16px', borderRadius: '16px', border: 'none', background: '#3D5C3C', fontSize: '14px', cursor: 'pointer', color: '#FFFFFF', textAlign: 'left', fontWeight: 600, boxShadow: 'var(--shadow-pill)' } : { padding: '13px 16px', borderRadius: '16px', border: 'none', background: '#F7F5EE', fontSize: '14px', cursor: 'pointer', color: '#3B4036', textAlign: 'left' }} onClick={() => setAnswer('overall_feeling', opt)}>{opt}</button>
            ))}
          </div>
        </div>

        {/* WHAT CHANGED */}
        <div className="sn-card" style={s.questionBlock}>
          <div style={s.questionLabel}>What changed this week? <span style={{ fontSize: '12px', color: '#7A7A72', fontWeight: 400 }}>(select all that apply)</span></div>
          <div style={s.chipRow}>
            {CONTEXT_OPTIONS.map(opt => (
              <button
                key={opt}
                style={(answers.context_changes || []).includes(opt) ? s.chipOn : s.chip}
                onClick={() => toggleContext(opt)}
              >{opt}</button>
            ))}
          </div>
        </div>

        {/* COMPLIANCE */}
        <div style={s.sectionDivider}>
          <div style={s.sectionLine} />
          <div className="sn-micro sn-micro-dim" style={s.sectionLabel}>Compliance</div>
          <div style={s.sectionLine} />
        </div>

        <div className="sn-card" style={s.questionBlock}>
          <div style={s.questionLabel}>How closely did you follow the elimination plan this week?</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {['Fully', 'Mostly', 'Some slip-ups', 'Not at all'].map(opt => (
              <button key={opt} style={answers.compliance === opt ? { padding: '13px 16px', borderRadius: '16px', border: 'none', background: '#3D5C3C', fontSize: '14px', cursor: 'pointer', color: '#FFFFFF', textAlign: 'left', fontWeight: 600, boxShadow: 'var(--shadow-pill)' } : { padding: '13px 16px', borderRadius: '16px', border: 'none', background: '#F7F5EE', fontSize: '14px', cursor: 'pointer', color: '#3B4036', textAlign: 'left' }} onClick={() => setAnswer('compliance', opt)}>{opt}</button>
            ))}
          </div>
        </div>

        {/* NOTES */}
        <div className="sn-card" style={s.questionBlock}>
          <div style={s.questionLabel}>Anything else worth noting? <span style={{ fontSize: '12px', color: '#7A7A72', fontWeight: 400 }}>(optional)</span></div>
          <textarea
            className="sn-input"
            style={s.textarea}
            placeholder="Anything unusual, a reaction you noticed, something you want your AI insight to factor in..."
            value={answers.notes || ''}
            onChange={e => setAnswer('notes', e.target.value)}
          />
        </div>
      </div>

      <div style={s.footer}>
        {submitError && <div style={{ background: '#FAEAEA', color: '#8B2E2E', borderRadius: 12, padding: '10px 13px', fontSize: 12.5, lineHeight: 1.55, marginBottom: 10 }}>{submitError}</div>}
        <button
          className="sn-btn"
          style={s.cta}
          disabled={!allRequiredAnswered() || submitting}
          onClick={handleSubmit}
        >
          {submitting ? 'Saving...' : 'Submit check-in →'}
        </button>
      </div>
    </div>
  )
}
